// Inc E — safe Markdown renderer for hotspot descriptions ("infografica").
//
// XSS-safe BY CONSTRUCTION: builds the output exclusively via
// document.createElement / textContent — NEVER innerHTML. The allowed subset is
// a strict allowlist; anything outside it renders as inert text. No images, no
// raw HTML, no headings (the popup is small). Links are restricted to an
// http/https/mailto allowlist (NOT dom-helpers.safeLink, which permits
// same-origin relative URLs); everything else degrades to plain text.
//
// Linear scans only (indexOf), no catastrophic-backtracking regex. Input is
// length-capped before parsing.

const MAX_SOURCE_LEN = 2000; // == backend DESCRIPTION_MAX

const DISALLOWED_URL_CHARS = /[\s<>"]/; // whitespace / angle brackets / quotes
const ALLOWED_URL_SCHEME = /^(https?:\/\/|mailto:)/i;

// Allowlist: only absolute http(s) / mailto. Relative, javascript:, data:,
// vbscript: etc. are rejected (rendered as plain text, no anchor). The char
// check intentionally does NOT include ':' or '/', so real URLs pass.
function isAllowedLinkUrl(url: string): boolean {
	const u = url.trim();
	if (DISALLOWED_URL_CHARS.test(u)) return false;
	return ALLOWED_URL_SCHEME.test(u);
}

// Parse inline markup into a list of DOM nodes. Recursive for emphasis/link
// text (bounded by input length). Code spans are literal (no nested parse).
function parseInline(text: string): Node[] {
	const nodes: Node[] = [];
	let buf = "";
	let i = 0;

	const flush = (): void => {
		if (buf.length > 0) {
			nodes.push(document.createTextNode(buf));
			buf = "";
		}
	};

	while (i < text.length) {
		const two = text.slice(i, i + 2);
		const ch = text[i] ?? "";

		if (two === "**") {
			const close = text.indexOf("**", i + 2);
			if (close > i + 1) {
				flush();
				const strong = document.createElement("strong");
				for (const n of parseInline(text.slice(i + 2, close)))
					strong.appendChild(n);
				nodes.push(strong);
				i = close + 2;
				continue;
			}
		} else if (ch === "*") {
			const close = text.indexOf("*", i + 1);
			if (close > i) {
				flush();
				const em = document.createElement("em");
				for (const n of parseInline(text.slice(i + 1, close)))
					em.appendChild(n);
				nodes.push(em);
				i = close + 1;
				continue;
			}
		} else if (ch === "`") {
			const close = text.indexOf("`", i + 1);
			if (close > i) {
				flush();
				const code = document.createElement("code");
				code.textContent = text.slice(i + 1, close); // literal, no nesting
				nodes.push(code);
				i = close + 1;
				continue;
			}
		} else if (ch === "[") {
			const closeBracket = text.indexOf("]", i + 1);
			if (closeBracket > i && text[closeBracket + 1] === "(") {
				const closeParen = text.indexOf(")", closeBracket + 2);
				if (closeParen > closeBracket) {
					const label = text.slice(i + 1, closeBracket);
					const url = text.slice(closeBracket + 2, closeParen);
					flush();
					if (isAllowedLinkUrl(url)) {
						const a = document.createElement("a");
						a.setAttribute("href", url.trim());
						a.setAttribute("target", "_blank");
						a.setAttribute("rel", "noopener noreferrer nofollow");
						for (const n of parseInline(label)) a.appendChild(n);
						nodes.push(a);
					} else {
						// Disallowed URL: render only the visible label as plain text.
						for (const n of parseInline(label)) nodes.push(n);
					}
					i = closeParen + 1;
					continue;
				}
			}
		}

		buf += ch;
		i += 1;
	}

	flush();
	return nodes;
}

const BULLET_RE = /^\s*[-*]\s+/;
const ORDERED_RE = /^\s*\d+\.\s+/;

function appendParagraph(frag: DocumentFragment, lines: string[]): void {
	const p = document.createElement("p");
	lines.forEach((line, idx) => {
		if (idx > 0) p.appendChild(document.createElement("br")); // soft line break
		for (const n of parseInline(line)) p.appendChild(n);
	});
	frag.appendChild(p);
}

function appendList(
	frag: DocumentFragment,
	lines: string[],
	ordered: boolean,
): void {
	const list = document.createElement(ordered ? "ol" : "ul");
	const re = ordered ? ORDERED_RE : BULLET_RE;
	for (const line of lines) {
		const li = document.createElement("li");
		for (const n of parseInline(line.replace(re, ""))) li.appendChild(n);
		list.appendChild(li);
	}
	frag.appendChild(list);
}

/**
 * Render a Markdown source string to a safe DocumentFragment. Subset:
 * paragraphs (blank-line separated), soft line breaks, `**bold**`, `*italic*`,
 * `` `code` ``, unordered (`-`/`*`) and ordered (`1.`) lists, and links
 * (http/https/mailto only). Everything else is inert text. Never uses innerHTML.
 */
export function renderMarkdownToFragment(source: string): DocumentFragment {
	const frag = document.createDocumentFragment();
	const src = source.slice(0, MAX_SOURCE_LEN).replace(/\r\n?/g, "\n");
	const blocks = src.split(/\n{2,}/);

	for (const rawBlock of blocks) {
		const block = rawBlock.replace(/\s+$/, "");
		if (block.trim().length === 0) continue;
		const lines = block.split("\n").filter((l) => l.trim().length > 0);
		if (lines.length === 0) continue;

		if (lines.every((l) => BULLET_RE.test(l))) {
			appendList(frag, lines, false);
		} else if (lines.every((l) => ORDERED_RE.test(l))) {
			appendList(frag, lines, true);
		} else {
			appendParagraph(frag, lines);
		}
	}

	return frag;
}

// Exposed for unit testing the URL allowlist in isolation.
export const __test = { isAllowedLinkUrl };
